import { useState, useCallback, useEffect, useRef } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { PersonSummary } from "../../api/trees";
import {
  ConnectedPersonSummary,
  ConnectedPersonCreate,
  listConnectedPersons,
  createConnectedPerson,
} from "../../api/connectedPersons";

interface PersonSelectorProps {
  treePersons: PersonSummary[];
  onSelect: (
    type: "tree" | "connected" | "name_only",
    id: number,
    name: string
  ) => void;
  onCancel: () => void;
  /** If true, show option to create new connected person */
  allowCreateConnected?: boolean;
  /** If true, show option to use text-only name */
  allowNameOnly?: boolean;
  /** Optional: pre-filter to show only certain types */
  filter?: "tree" | "connected" | "all";
  /** Placeholder text for search input */
  placeholder?: string;
}

type ViewMode = "search" | "create-connected" | "name-only";

export default function PersonSelector({
  treePersons,
  onSelect,
  onCancel,
  allowCreateConnected = true,
  allowNameOnly = true,
  filter = "all",
  placeholder = "Search for a person...",
}: PersonSelectorProps) {
  const { getAccessTokenSilently } = useAuth0();
  const inputRef = useRef<HTMLInputElement>(null);

  const [viewMode, setViewMode] = useState<ViewMode>("search");
  const [search, setSearch] = useState("");
  const [connectedPersons, setConnectedPersons] = useState<ConnectedPersonSummary[]>([]);
  const [isLoadingConnected, setIsLoadingConnected] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // For name-only mode
  const [nameOnlyValue, setNameOnlyValue] = useState("");

  // Form state for creating connected person
  const [newPerson, setNewPerson] = useState<ConnectedPersonCreate>({
    given_name: "",
    surname: "",
    sex: "",
    notes: "",
  });

  const loadConnectedPersons = useCallback(async () => {
    try {
      setIsLoadingConnected(true);
      const token = await getAccessTokenSilently();
      const persons = await listConnectedPersons(token, undefined, 100);
      setConnectedPersons(persons);
    } catch (err) {
      console.error("Failed to load connected persons:", err);
    } finally {
      setIsLoadingConnected(false);
    }
  }, [getAccessTokenSilently]);

  // Load connected persons on mount
  useEffect(() => {
    if (filter === "all" || filter === "connected") {
      loadConnectedPersons();
    }
  }, [filter, loadConnectedPersons]);

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, [viewMode]);

  // Filter tree persons
  const filteredTreePersons = treePersons.filter((p) => {
    if (filter === "connected") return false;
    if (!search) return true;
    const name = p.full_name || `${p.given_name || ""} ${p.surname || ""}`;
    return name.toLowerCase().includes(search.toLowerCase());
  });

  // Filter connected persons
  const filteredConnectedPersons = connectedPersons.filter((p) => {
    if (filter === "tree") return false;
    if (!search) return true;
    const name = p.full_name || `${p.given_name || ""} ${p.surname || ""}`;
    return name.toLowerCase().includes(search.toLowerCase());
  });

  const handleSelectTreePerson = (person: PersonSummary) => {
    const name = person.full_name || `${person.given_name || ""} ${person.surname || ""}`.trim();
    onSelect("tree", person.id, name);
  };

  const handleSelectConnectedPerson = (person: ConnectedPersonSummary) => {
    const name = person.full_name || `${person.given_name || ""} ${person.surname || ""}`.trim();
    onSelect("connected", person.id, name);
  };

  const handleCreateConnected = async () => {
    if (!newPerson.given_name && !newPerson.surname) {
      setError("Please enter at least a first or last name");
      return;
    }

    try {
      setIsCreating(true);
      setError(null);
      const token = await getAccessTokenSilently();
      const created = await createConnectedPerson(newPerson, token);
      const name = created.full_name || `${created.given_name || ""} ${created.surname || ""}`.trim();
      onSelect("connected", created.id, name);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create person");
    } finally {
      setIsCreating(false);
    }
  };

  const getPersonName = (p: PersonSummary | ConnectedPersonSummary) => {
    return p.full_name || `${p.given_name || ""} ${p.surname || ""}`.trim() || "Unknown";
  };

  // Format birth/death years for display (e.g., "1850-1920" or "b. 1850" or "d. 1920")
  const getPersonDates = (p: PersonSummary): string => {
    const birthYear = p.birth_date?.substring(0, 4) || p.birth_date_text?.match(/\d{4}/)?.[0];
    const deathYear = p.death_date?.substring(0, 4) || p.death_date_text?.match(/\d{4}/)?.[0];
    
    if (birthYear && deathYear) {
      return `${birthYear}–${deathYear}`;
    } else if (birthYear) {
      return `b. ${birthYear}`;
    } else if (deathYear) {
      return `d. ${deathYear}`;
    }
    return "";
  };

  // Get tree name if available
  const getTreeInfo = (p: PersonSummary): string => {
    return p.tree_name || "";
  };

  // Handle name-only submission
  const handleNameOnlySubmit = () => {
    const trimmedName = nameOnlyValue.trim();
    if (!trimmedName) {
      setError("Please enter a name");
      return;
    }
    // Use 0 as a placeholder ID for name-only tags (backend ignores it)
    onSelect("name_only", 0, trimmedName);
  };

  if (viewMode === "name-only") {
    return (
      <div className="w-80 rounded-lg bg-white shadow-xl border p-4">
        <h3 className="font-medium text-vault-900 mb-3">Tag with Name Only</h3>
        <p className="text-xs text-vault-500 mb-3">
          Enter a name to tag this person. This creates a text-only tag without linking
          to a family tree member or connected person.
        </p>

        {error && (
          <div className="mb-3 p-2 text-sm text-red-600 bg-red-50 rounded">
            {error}
          </div>
        )}

        <input
          ref={inputRef}
          type="text"
          placeholder="Enter name..."
          className="w-full rounded border border-vault-200 px-3 py-2 text-sm mb-3"
          value={nameOnlyValue}
          onChange={(e) => {
            setNameOnlyValue(e.target.value);
            setError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleNameOnlySubmit();
          }}
        />

        <div className="flex gap-2">
          <button
            onClick={handleNameOnlySubmit}
            className="flex-1 rounded bg-gray-600 px-4 py-2 text-sm text-white hover:bg-gray-700"
          >
            Add Name Tag
          </button>
          <button
            onClick={() => {
              setViewMode("search");
              setNameOnlyValue("");
              setError(null);
            }}
            className="rounded bg-vault-100 px-4 py-2 text-sm text-vault-600 hover:bg-vault-200"
          >
            Back
          </button>
        </div>
      </div>
    );
  }

  if (viewMode === "create-connected") {
    return (
      <div className="w-80 rounded-lg bg-white shadow-xl border p-4">
        <h3 className="font-medium text-vault-900 mb-3">Add Connected Person</h3>
        <p className="text-xs text-vault-500 mb-3">
          A connected person is someone not in your family tree but associated with family members
          (e.g., a friend, neighbor, or colleague).
        </p>

        {error && (
          <div className="mb-3 p-2 text-sm text-red-600 bg-red-50 rounded">
            {error}
          </div>
        )}

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <input
              ref={inputRef}
              type="text"
              placeholder="First name"
              className="w-full rounded border border-vault-200 px-3 py-2 text-sm"
              value={newPerson.given_name}
              onChange={(e) => setNewPerson({ ...newPerson, given_name: e.target.value })}
            />
            <input
              type="text"
              placeholder="Last name"
              className="w-full rounded border border-vault-200 px-3 py-2 text-sm"
              value={newPerson.surname}
              onChange={(e) => setNewPerson({ ...newPerson, surname: e.target.value })}
            />
          </div>

          <select
            className="w-full rounded border border-vault-200 px-3 py-2 text-sm"
            value={newPerson.sex}
            onChange={(e) => setNewPerson({ ...newPerson, sex: e.target.value })}
          >
            <option value="">Select sex (optional)</option>
            <option value="M">Male</option>
            <option value="F">Female</option>
          </select>

          <textarea
            placeholder="Notes (optional)"
            className="w-full rounded border border-vault-200 px-3 py-2 text-sm"
            rows={2}
            value={newPerson.notes}
            onChange={(e) => setNewPerson({ ...newPerson, notes: e.target.value })}
          />
        </div>

        <div className="flex gap-2 mt-4">
          <button
            onClick={handleCreateConnected}
            disabled={isCreating}
            className="flex-1 rounded bg-vault-600 px-4 py-2 text-sm text-white hover:bg-vault-700 disabled:opacity-50"
          >
            {isCreating ? "Creating..." : "Create & Select"}
          </button>
          <button
            onClick={() => setViewMode("search")}
            className="rounded bg-vault-100 px-4 py-2 text-sm text-vault-600 hover:bg-vault-200"
          >
            Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-80 rounded-lg bg-white shadow-xl border p-4">
      <h3 className="font-medium text-vault-900 mb-2">Select Person</h3>

      <input
        ref={inputRef}
        type="text"
        placeholder={placeholder}
        className="w-full rounded border border-vault-200 px-3 py-2 text-sm mb-2"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      <div className="max-h-60 overflow-y-auto">
        {/* Tree Persons Section */}
        {filter !== "connected" && filteredTreePersons.length > 0 && (
          <>
            <div className="sticky top-0 bg-vault-100 px-3 py-1 text-xs font-medium text-vault-600">
              Family Tree Members
            </div>
            {filteredTreePersons.slice(0, 30).map((person, idx) => {
              const dates = getPersonDates(person);
              const treeName = getTreeInfo(person);
              return (
                <button
                  key={`tree-${person.id}-${person.tree_id || idx}`}
                  type="button"
                  className="w-full text-left px-3 py-2 text-sm hover:bg-vault-50 rounded flex items-center gap-2 cursor-pointer"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleSelectTreePerson(person);
                  }}
                >
                  <span className={`w-6 h-6 flex-shrink-0 rounded-full flex items-center justify-center text-xs ${
                    person.sex === "M" ? "bg-blue-100 text-blue-700" : "bg-pink-100 text-pink-700"
                  }`}>
                    {person.sex === "M" ? "♂" : "♀"}
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block truncate">{getPersonName(person)}</span>
                    {(dates || treeName) && (
                      <span className="block text-xs text-vault-400 truncate">
                        {dates}{dates && treeName ? " • " : ""}{treeName}
                      </span>
                    )}
                  </span>
                </button>
              );
            })}
          </>
        )}

        {/* Connected Persons Section */}
        {filter !== "tree" && (
          <>
            {isLoadingConnected ? (
              <div className="px-3 py-2 text-sm text-vault-500">Loading...</div>
            ) : filteredConnectedPersons.length > 0 ? (
              <>
                <div className="sticky top-0 bg-amber-100 px-3 py-1 text-xs font-medium text-amber-700 mt-2">
                  Connected People (Non-Family)
                </div>
                {filteredConnectedPersons.slice(0, 30).map((person) => {
                  // ConnectedPersonSummary may have birth_date_text, death_date_text
                  const birthYear = (person as PersonSummary).birth_date?.substring(0, 4) || 
                    (person as PersonSummary).birth_date_text?.match(/\d{4}/)?.[0];
                  const deathYear = (person as PersonSummary).death_date?.substring(0, 4) || 
                    (person as PersonSummary).death_date_text?.match(/\d{4}/)?.[0];
                  let dates = "";
                  if (birthYear && deathYear) dates = `${birthYear}–${deathYear}`;
                  else if (birthYear) dates = `b. ${birthYear}`;
                  else if (deathYear) dates = `d. ${deathYear}`;
                  
                  return (
                    <button
                      key={`connected-${person.id}`}
                      type="button"
                      className="w-full text-left px-3 py-2 text-sm hover:bg-amber-50 rounded flex items-center gap-2 cursor-pointer"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleSelectConnectedPerson(person);
                      }}
                    >
                      <span className="w-6 h-6 flex-shrink-0 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center text-xs">
                        ⚭
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className="block truncate">{getPersonName(person)}</span>
                        {dates && (
                          <span className="block text-xs text-amber-500 truncate">{dates}</span>
                        )}
                      </span>
                    </button>
                  );
                })}
              </>
            ) : null}
          </>
        )}

        {/* Empty state - show helpful message when search has no results */}
        {filteredTreePersons.length === 0 && filteredConnectedPersons.length === 0 && !isLoadingConnected && (
          <div className="px-3 py-2">
            <p className="text-sm text-vault-500">No matching persons found</p>
            {search && (
              <p className="text-xs text-vault-400 mt-1">
                Use the buttons below to add &quot;{search}&quot; as a new person
              </p>
            )}
          </div>
        )}
      </div>

      {/* Create Connected Person option */}
      {allowCreateConnected && filter !== "tree" && (
        <button
          onClick={() => {
            // Pre-fill name from search if available
            if (search.trim()) {
              const parts = search.trim().split(/\s+/);
              const givenName = parts[0] || "";
              const surname = parts.slice(1).join(" ") || "";
              setNewPerson({ ...newPerson, given_name: givenName, surname: surname });
            }
            setViewMode("create-connected");
          }}
          className="mt-2 w-full rounded border-2 border-dashed border-vault-300 px-3 py-2 text-sm text-vault-600 hover:bg-vault-50 hover:border-vault-400"
        >
          + Add Non-Family Person{search.trim() ? ` "${search.trim()}"` : ""}
        </button>
      )}

      {/* Name-only option */}
      {allowNameOnly && (
        <button
          onClick={() => {
            // Pre-fill name from search if available
            if (search.trim()) {
              setNameOnlyValue(search.trim());
            }
            setViewMode("name-only");
          }}
          className="mt-2 w-full rounded border-2 border-dashed border-gray-300 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 hover:border-gray-400"
        >
          📝 Just enter a name (no link){search.trim() ? ` "${search.trim()}"` : ""}
        </button>
      )}

      <button
        onClick={onCancel}
        className="mt-2 w-full rounded bg-vault-100 px-3 py-2 text-sm text-vault-600 hover:bg-vault-200"
      >
        Cancel
      </button>
    </div>
  );
}
